import { type StatusBadgeTone } from '@/app/modules/table/components/StatusBadge';
import {
  type ChecklistAnswer,
  type ChecklistAnswerInput,
  type ChecklistOption,
  ChecklistListTab,
  ChecklistOrigin,
  ChecklistQuestionType,
  type ChecklistQuestion,
  type ChecklistRun,
  type ChecklistRunDetail,
  ChecklistRunStatus,
  type ChecklistScore,
  type ChecklistSection,
  type ChecklistSectionScore,
  type ChecklistVisitEvent,
} from './_models';

const LOW_SCORE_PERCENT = 75;

export function isLowChecklistScore(percent: number): boolean {
  return percent < LOW_SCORE_PERCENT;
}

type StatusBadgeConfig = { label: string; tone: StatusBadgeTone };

const STATUS_BADGES: Record<ChecklistRunStatus, StatusBadgeConfig> = {
  [ChecklistRunStatus.PENDING]: { label: 'Pendente', tone: 'warning' },
  [ChecklistRunStatus.IN_PROGRESS]: { label: 'Em andamento', tone: 'info' },
  [ChecklistRunStatus.LATE]: { label: 'Atrasado', tone: 'danger' },
  [ChecklistRunStatus.SENT]: { label: 'Enviado', tone: 'success' },
  [ChecklistRunStatus.NOT_DONE]: { label: 'Não realizado', tone: 'danger' },
  [ChecklistRunStatus.CANCELED]: { label: 'Cancelado', tone: 'secondary' },
};

const HISTORY_STATUSES = new Set([ChecklistRunStatus.SENT, ChecklistRunStatus.NOT_DONE, ChecklistRunStatus.CANCELED]);

export function checklistStatusBadge(status: ChecklistRunStatus): StatusBadgeConfig {
  return STATUS_BADGES[status];
}

export function isChecklistClosed(status: ChecklistRunStatus): boolean {
  return HISTORY_STATUSES.has(status);
}

export function findBlockingChecklistRun(activityRuns: ChecklistRun[]): ChecklistRun | undefined {
  const openRuns = activityRuns.filter((run) => !isChecklistClosed(run.status));
  const mandatoryRun = openRuns.find((run) => run.activity?.mandatory === true);
  if (mandatoryRun) return mandatoryRun;
  return openRuns.find((run) => run.activity?.mandatory === false);
}

export function runsInTab(runs: ChecklistRun[], tab: ChecklistListTab): ChecklistRun[] {
  if (tab === ChecklistListTab.ALL) return runs;
  return runs.filter((run) => !isChecklistClosed(run.status));
}

export function pendingRunsCount(runs: ChecklistRun[]): number {
  return runsInTab(runs, ChecklistListTab.PENDING).length;
}

function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatChecklistTime(iso: string): string {
  const date = new Date(iso);
  return `${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`;
}

function formatChecklistDay(date: Date): string {
  return `${twoDigits(date.getDate())}/${twoDigits(date.getMonth() + 1)}`;
}

function dayStart(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function checklistDueLabel(dueAt: string, now: Date): string {
  const due = new Date(dueAt);
  const time = formatChecklistTime(dueAt);
  const overdue = due.getTime() < now.getTime();
  const dayDifference = Math.round((dayStart(due) - dayStart(now)) / 86_400_000);
  if (dayDifference === 0) return overdue ? `venceu hoje, ${time}` : `hoje até ${time}`;
  if (dayDifference === -1 && overdue) return `venceu ontem, ${time}`;
  const day = formatChecklistDay(due);
  return overdue ? `venceu ${day}, ${time}` : `até ${day}, ${time}`;
}

export function checklistOriginLabel(origin: ChecklistOrigin, activityLabel?: string): string {
  if (origin === ChecklistOrigin.ACTIVITY) return activityLabel ?? 'Atividade';
  if (origin === ChecklistOrigin.SCHEDULE) return 'Agendado';
  return 'Franqueadora';
}

export function checklistMetaLabel(
  store: string,
  origin: ChecklistOrigin,
  activityLabel: string | undefined,
  dueAt: string,
  now: Date,
): string {
  const originLabel = checklistOriginLabel(origin, activityLabel);
  const dueLabel = checklistDueLabel(dueAt, now);
  return `${store} · ${originLabel} · ${dueLabel}`;
}

export function checklistMetaClass(status: ChecklistRunStatus): string {
  const late = status === ChecklistRunStatus.LATE;
  return late ? 'text-danger fw-bold' : 'text-gray-600 fw-semibold';
}

export function checklistSentAtLabel(sentAt?: string): string {
  if (!sentAt) return '—';
  const sentDay = formatChecklistDay(new Date(sentAt));
  return `${sentDay}, ${formatChecklistTime(sentAt)}`;
}

export function checklistRespondedBy(sentBy?: string, startedBy?: string, appliedBy?: string): string {
  return sentBy ?? startedBy ?? appliedBy ?? '—';
}

export function checklistDateLabel(
  status: ChecklistRunStatus,
  sentAt: string | undefined,
  dueAt: string,
  now: Date,
): string {
  if (isChecklistClosed(status)) return checklistSentAtLabel(sentAt);
  return checklistDueLabel(dueAt, now);
}

export function findChecklistQuestion(sections: ChecklistSection[], questionId: string): ChecklistQuestion | undefined {
  const questions = sections.flatMap((section) => section.questions);
  return questions.find((question) => question.id === questionId);
}

export function buildChecklistAnswer(
  options: ChecklistOption[],
  input: ChecklistAnswerInput,
  actor: string,
  answeredAt: string,
): ChecklistAnswer {
  const answer: ChecklistAnswer = {
    questionId: input.questionId,
    value: input.notApplicable ? undefined : input.value,
    notApplicable: input.notApplicable,
    comment: input.comment,
    photos: input.photos,
    answeredAt,
    answeredBy: actor,
    problem: false,
  };
  if (input.notApplicable || options.length === 0) return answer;
  const chosen = options.find((option) => option.value === input.value);
  answer.problem = chosen?.problem ?? false;
  answer.points = chosen?.points;
  const optionPoints = options.map((option) => option.points);
  answer.maxPoints = Math.max(...optionPoints);
  return answer;
}

export function isChecklistAnswered(
  type: ChecklistQuestionType,
  allowsNotApplicable: boolean,
  notApplicable: boolean,
  photoCount: number,
  value?: string,
): boolean {
  if (notApplicable && allowsNotApplicable) return true;
  if (type === ChecklistQuestionType.PHOTO) return photoCount > 0;
  return Boolean(value);
}

function isQuestionAnswered(
  questionId: string,
  type: ChecklistQuestionType,
  allowsNotApplicable: boolean,
  answers: ChecklistAnswer[],
): boolean {
  const answer = answers.find((item) => item.questionId === questionId);
  if (!answer) return false;
  return isChecklistAnswered(type, allowsNotApplicable, answer.notApplicable, answer.photos.length, answer.value);
}

export function countAnsweredQuestions(sections: ChecklistSection[], answers: ChecklistAnswer[]): number {
  const questions = sections.flatMap((section) => section.questions);
  const answeredQuestions = questions.filter((question) =>
    isQuestionAnswered(question.id, question.type, question.allowsNotApplicable, answers),
  );
  return answeredQuestions.length;
}

export function countChecklistQuestions(sections: ChecklistSection[]): number {
  return sections.reduce((total, section) => total + section.questions.length, 0);
}

export function countPhotoQuestions(sections: ChecklistSection[]): number {
  const questions = sections.flatMap((section) => section.questions);
  const photoQuestions = questions.filter(
    (question) => question.photoRequired || question.type === ChecklistQuestionType.PHOTO,
  );
  return photoQuestions.length;
}

export function missingRequiredNumbers(sections: ChecklistSection[], answers: ChecklistAnswer[]): string[] {
  const questions = sections.flatMap((section) => section.questions);
  const missing = questions.filter((question) => {
    const answer = answers.find((item) => item.questionId === question.id);
    if (answer?.notApplicable && question.allowsNotApplicable) return false;
    const missingValue =
      question.required && !isQuestionAnswered(question.id, question.type, question.allowsNotApplicable, answers);
    const missingPhoto = question.photoRequired && (answer?.photos.length ?? 0) === 0;
    return missingValue || missingPhoto;
  });
  return missing.map((question) => question.number);
}

export function scoreChecklist(templateSections: ChecklistSection[], answers: ChecklistAnswer[]): ChecklistScore {
  const sections: ChecklistSectionScore[] = templateSections.map((section) => {
    const sectionAnswers = answers.filter((answer) =>
      section.questions.some((question) => question.id === answer.questionId),
    );
    const points = sectionAnswers.reduce((total, answer) => total + (answer.points ?? 0), 0);
    const maxPoints = sectionAnswers.reduce((total, answer) => total + (answer.maxPoints ?? 0), 0);
    const problems = sectionAnswers.filter((answer) => answer.problem).length;
    const sectionPercent = maxPoints === 0 ? 0 : Math.round((points / maxPoints) * 100);
    return { sectionId: section.id, points, maxPoints, percent: sectionPercent, problems };
  });
  const points = sections.reduce((total, section) => total + section.points, 0);
  const maxPoints = sections.reduce((total, section) => total + section.maxPoints, 0);
  const problems = sections.reduce((total, section) => total + section.problems, 0);
  const percent = maxPoints === 0 ? 0 : Math.round((points / maxPoints) * 100);
  const notApplicable = answers.filter((answer) => answer.notApplicable).length;
  const photos = answers.reduce((total, answer) => total + answer.photos.length, 0);
  return { points, maxPoints, percent, problems, notApplicable, photos, sections };
}

export function buildVisitTimeline(detail: ChecklistRunDetail): ChecklistVisitEvent[] {
  const events: ChecklistVisitEvent[] = [];
  if (detail.startedAt) {
    const place = detail.startLocation ? ` · ${detail.startLocation}` : '';
    events.push({
      time: formatChecklistTime(detail.startedAt),
      title: 'Checklist começado',
      detail: `${detail.startedBy ?? ''}${place}`,
      tone: 'primary',
    });
  }
  detail.template.sections.forEach((section, index) => {
    const sectionAnswers = detail.answers.filter((answer) =>
      section.questions.some((question) => question.id === answer.questionId),
    );
    const answeredQuestions = section.questions.filter((question) =>
      isQuestionAnswered(question.id, question.type, question.allowsNotApplicable, sectionAnswers),
    );
    if (answeredQuestions.length === 0) return;
    const answeredTimes = sectionAnswers
      .map((answer) => answer.answeredAt)
      .sort((first, second) => first.localeCompare(second));
    const lastAnsweredAt = answeredTimes[answeredTimes.length - 1];
    const problems = sectionAnswers.filter((answer) => answer.problem).length;
    const problemLabel = problems > 0 ? ` · ${problems} com problema` : '';
    const authors = [...new Set(sectionAnswers.map((answer) => answer.answeredBy))].join(', ');
    const photoCount = sectionAnswers.reduce((total, answer) => total + answer.photos.length, 0);
    const photoUnit = photoCount === 1 ? 'foto' : 'fotos';
    const photoLabel = photoCount > 0 ? ` · ${photoCount} ${photoUnit}` : '';
    const answeredLabel = `${answeredQuestions.length} de ${section.questions.length} respondidas`;
    events.push({
      time: formatChecklistTime(lastAnsweredAt),
      title: `${index + 1}. ${section.title}`,
      detail: `${answeredLabel} · ${authors}${photoLabel}${problemLabel}`,
      tone: problems > 0 ? 'danger' : 'success',
    });
  });
  if (detail.checkout) {
    events.push({
      time: formatChecklistTime(detail.checkout.signedAt),
      title: 'Comentário final e assinatura',
      detail: detail.checkout.signedBy,
      tone: 'success',
    });
  }
  if (detail.sentAt) {
    const scoreLabel = detail.score ? ` · nota ${detail.score.percent}%` : '';
    events.push({
      time: formatChecklistTime(detail.sentAt),
      title: 'Checklist enviado',
      detail: `${detail.sentBy ?? ''}${scoreLabel}`,
      tone: 'success',
    });
  }
  return events;
}
